import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Circle from './components/circle';
import { themes } from './constants';
import styles from './progress-ring.css';

class ProgressRing extends Component {
    state = {
        progress: 0,
        adjustment: 0,
        isResetting: false,
    };

    componentDidMount() {
        const { value, maxValue, adjustmentValue } = this.props;
        this.draw(value, maxValue, adjustmentValue);
    }

    componentDidUpdate(prevProps) {
        const { value, maxValue, adjustmentValue, resetRingOnChange } = this.props;
        const redraw =
            prevProps.value !== value ||
            prevProps.maxValue !== maxValue ||
            prevProps.adjustmentValue !== adjustmentValue;

        if (redraw && resetRingOnChange) {
            this.resetCircles();
        } else if (redraw) {
            this.drawNextRing(value, maxValue, adjustmentValue);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.positiveTimeout);
        clearTimeout(this.negativeTimeout);
    }

    static themes = themes;

    draw(value, maxValue, adjustmentValue) {
        if (adjustmentValue === 0) {
            this.setProgress(value, maxValue);
        }

        if (adjustmentValue > 0) {
            this.drawPositiveAdjustment(value, maxValue, adjustmentValue);
        }

        if (adjustmentValue < 0) {
            this.drawNegativeAdjustment(value, maxValue, adjustmentValue);
        }
    }

    resetCircles() {
        this.setState({
            progress: 0,
            adjustment: 0,
            isResetting: true,
        });
    }

    drawNextRing(value, maxValue, adjustmentValue) {
        this.setState({ isResetting: false });
        this.draw(value, maxValue, adjustmentValue);
    }

    setProgress = (value, maxValue) => {
        this.setState({ progress: value / maxValue || 0 });
    };

    setAdjustment = (value, maxValue, adjustmentValue) => {
        this.setState({ adjustment: Math.abs(adjustmentValue / maxValue) });
    };

    drawPositiveAdjustment(value, maxValue, adjustmentValue) {
        this.setProgress(value, maxValue);
        this.positiveTimeout = setTimeout(() => this.setAdjustment(value, maxValue, adjustmentValue), 1000);
    }

    drawNegativeAdjustment(value, maxValue, adjustmentValue) {
        this.setProgress(value - adjustmentValue, maxValue);
        this.negativeTimeout = setTimeout(() => {
            this.setAdjustment(value, maxValue, adjustmentValue);
            this.setProgress(value, maxValue);
        }, 1000);
    }

    // eslint-disable-next-line class-methods-use-this
    getThemeAdjustment(theme, adjustmentValue) {
        if (theme === themes.SHORT_TERM_DEBT) return adjustmentValue < 0 ? 'POSITIVE' : 'NEGATIVE';
        return adjustmentValue > 0 ? 'POSITIVE' : 'NEGATIVE';
    }

    render() {
        const { value, maxValue, theme, adjustmentValue, duration, strokeWidth, onComplete } = this.props;
        const { progress, adjustment, isResetting } = this.state;
        const circleTransitionDuration = isResetting ? 1000 : duration;
        const adjustmentTransition = adjustmentValue < 0 ? 0 : duration;
        const adjustmentTransitionDuration = isResetting ? 0 : adjustmentTransition;
        const circleSize = 100;
        const adjustmentRotation = (value / maxValue) * 360 - 90;

        return (
            <svg
                className={cx(styles.svg, { [styles.isResetting]: isResetting })}
                viewBox={`0 0 ${circleSize} ${circleSize}`}
                onTransitionEnd={() => this.drawNextRing(value, maxValue, adjustmentValue)}
            >
                <If condition={adjustmentValue !== 0}>
                    <Circle
                        adjustmentRotation={adjustmentRotation}
                        progress={adjustment}
                        theme={this.getThemeAdjustment(theme, adjustmentValue)}
                        strokeWidth={strokeWidth}
                        size={circleSize}
                        duration={adjustmentTransitionDuration}
                        onComplete={onComplete}
                    />
                </If>

                <Circle
                    progress={progress}
                    theme={theme}
                    strokeWidth={strokeWidth}
                    size={circleSize}
                    duration={circleTransitionDuration}
                    onComplete={onComplete}
                />
            </svg>
        );
    }
}

ProgressRing.propTypes = {
    value: PropTypes.number.isRequired,
    maxValue: PropTypes.number.isRequired,
    theme: PropTypes.oneOf(Object.values(themes)).isRequired,
    adjustmentValue: PropTypes.number,
    duration: PropTypes.number,
    strokeWidth: PropTypes.number,
    onComplete: PropTypes.func,
    resetRingOnChange: PropTypes.bool,
};

ProgressRing.defaultProps = {
    resetRingOnChange: true,
    adjustmentValue: 0,
    duration: 1000,
    strokeWidth: 2,
    onComplete: () => null,
};

export default ProgressRing;
